import {
  type MeiaCatalogRedForceActivitySelectDto,
  type StudyMissionRedActivityBindingSelectDto,
  type StudyMissionSelectDto,
  StudyMissionStatus,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { ConfirmDeleteModal, ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { Label } from '@components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Skeleton } from '@components/ui/skeleton';
import { Textarea } from '@components/ui/textarea';
import { successToast } from '@hooks/toast.hook';
import {
  useCreateMeiaMissionRedActivityBinding,
  useDeleteMeiaMissionRedActivityBinding,
  useGetMeiaMissionRedActivityBindings,
  useGetMeiaRedForceActivities,
  useTransitionMeiaStudyMissionStatus,
} from '@queries/meia.query';
import { formatEnumLabel } from '@utils/meia-enum.util';
import { Link2Icon, ShieldAlertIcon, Trash2Icon } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { MeiaInnerSection } from './meia-inner-section.component';
import {
  MISSION_STATUS_REASON_MIN_LENGTH,
  getConfidenceTierBadgeVariant,
  getConfidenceTierDescription,
  getConfidenceTierLabel,
  getNextStudyMissionStatuses,
  getRedForceActivityProvenanceSourceDescription,
  getRedForceActivityProvenanceSourceLabel,
  getRedForceActivityTierLabel,
  getStudyMissionStatusBadgeVariant,
  getStudyMissionStatusLabel,
  shouldRequireMissionCloseGateAcknowledgement,
  shouldRequireMissionReopenReason,
} from './meia-red-force-activities.util';

type MeiaRedForceActivitiesTileProps = {
  canEdit: boolean;
  mission: StudyMissionSelectDto;
  studyId: string;
};

type RedForceActivityBindingRowProps = {
  activity?: MeiaCatalogRedForceActivitySelectDto;
  binding: StudyMissionRedActivityBindingSelectDto;
  canEdit: boolean;
  onRemove: (binding: StudyMissionRedActivityBindingSelectDto) => void;
};

type RedForceActivityPickerCardProps = {
  activity: MeiaCatalogRedForceActivitySelectDto;
  isBinding: boolean;
  onBind: (activity: MeiaCatalogRedForceActivitySelectDto) => void;
};

type TierFilterValue = 'all' | '1' | '2' | '3';

const TIER_FILTER_OPTIONS: Array<{ label: string; value: TierFilterValue }> = [
  { label: 'All tiers', value: 'all' },
  { label: 'Strategic', value: '1' },
  { label: 'Operational', value: '2' },
  { label: 'Tactical', value: '3' },
];

const getActivityDisplayName = (activity: MeiaCatalogRedForceActivitySelectDto | undefined, code: string): string =>
  activity?.shortName ?? activity?.name ?? code;

const RedForceActivityMetaBadges: FC<{ activity: MeiaCatalogRedForceActivitySelectDto }> = (props) => {
  const { activity } = props;

  return (
    <>
      <TooltipComponent
        trigger={
          <span className="inline-flex cursor-help">
            <Badge
              size="xs"
              variant="secondary"
            >
              {getRedForceActivityTierLabel(activity.tier)}
            </Badge>
          </span>
        }
      >
        Activity tier — the level of war this opposing activity operates at (Strategic, Operational, or Tactical).
      </TooltipComponent>
      <TooltipComponent
        trigger={
          <span className="inline-flex cursor-help">
            <Badge
              size="xs"
              variant={getConfidenceTierBadgeVariant(activity.provenance.confidenceTier)}
            >
              {getConfidenceTierLabel(activity.provenance.confidenceTier)}
            </Badge>
          </span>
        }
      >
        {getConfidenceTierDescription(activity.provenance.confidenceTier)}
      </TooltipComponent>
      <TooltipComponent
        trigger={
          <span className="inline-flex cursor-help">
            <Badge
              size="xs"
              variant="outline"
            >
              {getRedForceActivityProvenanceSourceLabel(activity.provenance.source)}
            </Badge>
          </span>
        }
      >
        {getRedForceActivityProvenanceSourceDescription(activity.provenance.source)}
      </TooltipComponent>
    </>
  );
};

const RedForceActivityKillChainBadges: FC<{ activity: MeiaCatalogRedForceActivitySelectDto }> = (props) => {
  const { activity } = props;

  return (
    <>
      {activity.associatedKillChainPhases.map((phase) => (
        <TooltipComponent
          key={phase}
          trigger={
            <span className="inline-flex cursor-help">
              <Badge
                size="xs"
                variant="destructive"
              >
                {formatEnumLabel(phase)}
              </Badge>
            </span>
          }
        >
          Kill-chain stage this red activity contests: {formatEnumLabel(phase)}.
        </TooltipComponent>
      ))}
    </>
  );
};

const RedForceActivityBindingRow: FC<RedForceActivityBindingRowProps> = (props) => {
  const { activity, binding, canEdit, onRemove } = props;

  return (
    <article className="flex flex-col gap-2.5 rounded-md border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h4 className="min-w-0 text-sm font-semibold leading-tight text-gray-800">
            {getActivityDisplayName(activity, binding.redForceActivityCode)}
          </h4>
          {activity && <p className="text-xs leading-relaxed text-gray-500">{activity.description}</p>}
        </div>
        {canEdit && (
          <IconButton
            ariaLabel={`Remove ${getActivityDisplayName(activity, binding.redForceActivityCode)}`}
            icon={<Trash2Icon />}
            onClick={() => onRemove(binding)}
            size="sm"
            title="Remove red-force activity"
            variant="destructive"
          />
        )}
      </div>

      {activity && (
        <div className="flex flex-wrap gap-1.5">
          <RedForceActivityMetaBadges activity={activity} />
          <RedForceActivityKillChainBadges activity={activity} />
        </div>
      )}

      {binding.rationale && (
        <p className="text-xs italic leading-relaxed text-gray-600">&ldquo;{binding.rationale}&rdquo;</p>
      )}
    </article>
  );
};

const RedForceActivityPickerCard: FC<RedForceActivityPickerCardProps> = (props) => {
  const { activity, isBinding, onBind } = props;

  return (
    <article className="flex items-start gap-3 rounded-md border border-gray-200 bg-white p-3 transition-colors hover:border-red-300">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <RedForceActivityMetaBadges activity={activity} />
        </div>
        <h4 className="text-sm font-medium text-gray-900">{activity.name}</h4>
        <p className="text-xs leading-relaxed text-gray-500">{activity.description}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <RedForceActivityKillChainBadges activity={activity} />
        </div>
      </div>
      <Button
        disabled={isBinding}
        onClick={() => onBind(activity)}
        size="sm"
        type="button"
        variant="text"
      >
        <Link2Icon className="size-3.5" />
        Bind
      </Button>
    </article>
  );
};

export const MeiaRedForceActivitiesTile: FC<MeiaRedForceActivitiesTileProps> = (props) => {
  const { canEdit, mission, studyId } = props;
  const [bindingToRemove, setBindingToRemove] = useState<StudyMissionRedActivityBindingSelectDto | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pendingCloseStatus, setPendingCloseStatus] = useState<StudyMissionStatus | null>(null);
  const [pendingReopen, setPendingReopen] = useState<{ reason: string; status: StudyMissionStatus } | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerTier, setPickerTier] = useState<TierFilterValue>('all');

  const { data: redForceActivities = [], isLoading: isLoadingActivities } = useGetMeiaRedForceActivities();
  const { data: redActivityBindings = [], isLoading: isLoadingBindings } = useGetMeiaMissionRedActivityBindings({
    missionId: mission.id,
    studyId,
  });
  const { isPending: isBinding, mutateAsync: createBinding } = useCreateMeiaMissionRedActivityBinding();
  const { isPending: isRemoving, mutateAsync: deleteBinding } = useDeleteMeiaMissionRedActivityBinding();
  const { isPending: isTransitioning, mutateAsync: transitionStatus } = useTransitionMeiaStudyMissionStatus();

  const activityByCode = useMemo(
    () => new Map(redForceActivities.map((activity) => [activity.id, activity])),
    [redForceActivities],
  );
  const boundActivityCodes = useMemo(
    () => new Set(redActivityBindings.map((binding) => binding.redForceActivityCode)),
    [redActivityBindings],
  );
  const availableActivities = useMemo(
    () => redForceActivities.filter((activity) => !boundActivityCodes.has(activity.id)),
    [boundActivityCodes, redForceActivities],
  );
  const pickerActivities = useMemo(() => {
    const search = pickerSearch.trim().toLowerCase();
    return availableActivities.filter((activity) => {
      if (pickerTier !== 'all' && String(activity.tier) !== pickerTier) return false;
      if (!search) return true;
      return [activity.id, activity.name, activity.shortName, activity.description].some((value) =>
        value.toLowerCase().includes(search),
      );
    });
  }, [availableActivities, pickerSearch, pickerTier]);
  const nextStatuses = getNextStudyMissionStatuses(mission.status);
  const isLoading = isLoadingActivities || isLoadingBindings;
  const reopenReasonLength = pendingReopen?.reason.trim().length ?? 0;
  const isReopenReasonValid = reopenReasonLength >= MISSION_STATUS_REASON_MIN_LENGTH;

  const closeAddModal = (): void => {
    setIsAddModalOpen(false);
    setPickerSearch('');
    setPickerTier('all');
  };

  const handleBindActivity = async (activity: MeiaCatalogRedForceActivitySelectDto): Promise<void> => {
    await createBinding({
      missionId: mission.id,
      redForceActivityCode: activity.id,
      studyId,
    });
    successToast({ description: `${activity.shortName} bound to mission.` });
    closeAddModal();
  };

  const handleConfirmRemove = async (): Promise<void> => {
    if (!bindingToRemove) return;

    await deleteBinding({
      bindingId: bindingToRemove.id,
      missionId: mission.id,
      studyId,
    });
    successToast({ description: 'Red-force activity removed.' });
    setBindingToRemove(null);
  };

  const handleTransitionStatus = async (status: StudyMissionStatus): Promise<void> => {
    if (shouldRequireMissionReopenReason(mission.status)) {
      setPendingReopen({ reason: '', status });
      return;
    }

    if (
      shouldRequireMissionCloseGateAcknowledgement({
        redActivityBindingCount: redActivityBindings.length,
        targetStatus: status,
      })
    ) {
      setPendingCloseStatus(status);
      return;
    }

    await transitionStatus({
      missionId: mission.id,
      status,
      studyId,
    });
    successToast({ description: `Mission status moved to ${getStudyMissionStatusLabel(status)}.` });
  };

  const handleConfirmCloseGate = async (): Promise<void> => {
    if (!pendingCloseStatus) return;

    await transitionStatus({
      acknowledgeCloseGate: true,
      missionId: mission.id,
      status: pendingCloseStatus,
      studyId,
    });
    successToast({ description: `Mission status moved to ${getStudyMissionStatusLabel(pendingCloseStatus)}.` });
    setPendingCloseStatus(null);
  };

  const handleConfirmReopen = async (): Promise<void> => {
    if (!pendingReopen || !isReopenReasonValid) return;

    await transitionStatus({
      missionId: mission.id,
      reason: pendingReopen.reason.trim(),
      status: pendingReopen.status,
      studyId,
    });
    successToast({ description: `Mission status moved to ${getStudyMissionStatusLabel(pendingReopen.status)}.` });
    setPendingReopen(null);
  };

  return (
    <>
      <MeiaInnerSection
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="text-xs font-medium text-gray-500">Status</span>
            <Badge
              size="xs"
              variant={getStudyMissionStatusBadgeVariant(mission.status)}
            >
              {getStudyMissionStatusLabel(mission.status)}
            </Badge>
            {canEdit &&
              nextStatuses.map((status) => (
                <Button
                  disabled={isTransitioning}
                  key={status}
                  onClick={() => void handleTransitionStatus(status)}
                  size="xs"
                  type="button"
                  variant="outline"
                >
                  <span aria-hidden>&rarr;</span>
                  {getStudyMissionStatusLabel(status)}
                </Button>
              ))}
          </div>
        }
        className="border-red-200 bg-red-50/40"
        title={`Opposing Red-Force Activities (${redActivityBindings.length})`}
      >
        <div className="flex flex-col gap-3">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : redActivityBindings.length > 0 ? (
            <div className="flex flex-col gap-2">
              {redActivityBindings.map((binding) => (
                <RedForceActivityBindingRow
                  activity={activityByCode.get(binding.redForceActivityCode)}
                  binding={binding}
                  canEdit={canEdit}
                  key={binding.id}
                  onRemove={setBindingToRemove}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-md border border-dashed border-red-200 bg-white px-3 py-3">
              <ShieldAlertIcon className="mt-0.5 size-4 shrink-0 text-red-400" />
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-gray-600">No opposing red-force activities bound.</p>
                <p className="text-xs leading-relaxed text-gray-500">
                  Closing this mission without bindings requires explicit acknowledgement.
                </p>
              </div>
            </div>
          )}

          {canEdit && (
            <Button
              className="self-start"
              disabled={isLoading || availableActivities.length === 0}
              onClick={() => setIsAddModalOpen(true)}
              size="sm"
              type="button"
              variant="text"
            >
              + Add Opposing Red Activity
            </Button>
          )}
        </div>
      </MeiaInnerSection>

      <Modal
        centered
        header={{
          subtitle: (
            <p className="text-xs text-gray-500">Pick an opposing activity from the library to bind to this mission.</p>
          ),
          title: 'Red Activity Library',
        }}
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        width="640px"
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <ClearableSearchInput
              className="min-w-52 flex-1"
              onChange={(value) => setPickerSearch(value ?? '')}
              placeholder="Search by name or description…"
              value={pickerSearch}
            />
            <Select
              onValueChange={(value) => setPickerTier(value as TierFilterValue)}
              value={pickerTier}
            >
              <SelectTrigger className="h-9 w-36 bg-white text-xs">
                <SelectValue placeholder="All tiers" />
              </SelectTrigger>
              <SelectContent>
                {TIER_FILTER_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto pr-1">
            {pickerActivities.length > 0 ? (
              pickerActivities.map((activity) => (
                <RedForceActivityPickerCard
                  activity={activity}
                  isBinding={isBinding}
                  key={activity.id}
                  onBind={(picked) => void handleBindActivity(picked)}
                />
              ))
            ) : (
              <p className="rounded-md border border-dashed border-gray-300 px-3 py-6 text-center text-xs text-gray-500">
                No unbound activities match this filter.
              </p>
            )}
          </div>

          <p className="text-xs text-gray-400">
            {availableActivities.length} of {redForceActivities.length} library activities available to bind.
          </p>
        </div>
      </Modal>

      <Modal
        centered
        header={{
          subtitle: <p className="text-xs text-gray-500">A reason is required to move a closed mission.</p>,
          title: pendingReopen ? `Reopen mission as ${getStudyMissionStatusLabel(pendingReopen.status)}?` : undefined,
        }}
        isOpen={Boolean(pendingReopen)}
        onClose={() => setPendingReopen(null)}
        width="560px"
      >
        {pendingReopen && (
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-gray-600">
              This mission is closed. Explain why it is being reopened — the note is saved with the mission and visible
              to reviewers. If the mission is closed again later, the close checks run again from scratch.
            </p>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label>Reason</Label>
                <span className="text-xs text-gray-400">
                  {reopenReasonLength} / {MISSION_STATUS_REASON_MIN_LENGTH} characters minimum
                </span>
              </div>
              <Textarea
                onChange={(event) => setPendingReopen({ ...pendingReopen, reason: event.target.value })}
                placeholder="e.g. New intel changed the threat picture, so the mission framing needs another review pass."
                rows={4}
                value={pendingReopen.reason}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                disabled={isTransitioning}
                onClick={() => setPendingReopen(null)}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                disabled={!isReopenReasonValid}
                loading={isTransitioning}
                onClick={() => void handleConfirmReopen()}
                type="button"
              >
                Move to {getStudyMissionStatusLabel(pendingReopen.status)}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={Boolean(bindingToRemove)}
        itemName="red-force activity binding"
        onConfirm={handleConfirmRemove}
        setIsOpen={(isOpen) => {
          if (!isOpen) setBindingToRemove(null);
        }}
      />

      <ConfirmationModal
        confirmLabel="Close mission"
        description={
          <p>
            This mission has no opposing red-force activities bound. Closing it now records that the gap is known and
            accepted.
          </p>
        }
        isOpen={Boolean(pendingCloseStatus)}
        isPending={isTransitioning || isRemoving}
        onConfirm={handleConfirmCloseGate}
        setIsOpen={(isOpen) => {
          if (!isOpen) setPendingCloseStatus(null);
        }}
        title="Close mission without red-force bindings?"
        variant="destructive"
      />
    </>
  );
};
